import { forwardRef, type ReactNode } from 'react'
import type { CheckboxProps as AriaCheckboxProps } from 'react-aria-components'
import {
  Checkbox as AriaCheckbox,
  composeRenderProps,
} from 'react-aria-components'
import { tv, type VariantProps } from 'tailwind-variants'
import MaterialSymbolsCheck from '~icons/material-symbols/check'

interface CheckboxMantineInputProps {
  value?: boolean
  onChange?: (v: boolean) => void
  error?: string
}

export const checkboxStyles = tv({
  slots: {
    root: 'group flex gap-2 cursor-pointer disabled:cursor-not-allowed',
    checkbox: [
      'size-4 rounded border-2 border-black/50 dark:border-white/50 flex items-center justify-center transition-colors',
      'group-hover:border-black/70 dark:group-hover:border-white/70',
      'group-focus-visible:outline outline-2 outline-offset-1 outline-black/80 dark:outline-white/80',
      'group-selected:bg-black dark:group-selected:bg-white group-selected:border-black dark:group-selected:border-white',
      'group-disabled:opacity-50',
    ],
    content: 'flex flex-col gap-1',
    label: 'text-sm leading-tight',
    description: 'text-xs text-black/60 dark:text-white/60 leading-tight',
    error: 'text-xs text-red-500 dark:text-red-400 leading-tight',
    checkIcon: [
      'size-3 text-white dark:text-black opacity-0 transition-opacity',
      'group-selected:opacity-100',
    ],
  },
  variants: {
    hasDescription: {
      true: {
        root: 'items-start',
        checkbox: 'mt-0.5',
      },
      false: {
        root: 'items-center',
      },
    },
  },
  defaultVariants: {
    hasDescription: false,
  },
})

export type UICheckboxProps = {
  /** The label text for the checkbox */
  children?: ReactNode
  /** Optional description text shown below the label */
  description?: ReactNode
  /** Whether the checkbox is checked */
  isSelected?: boolean
  /** Called when the checkbox selection changes */
  onChange?: (isSelected: boolean) => void

  // Mantine form compatibility props
  /** Mantine form checked prop (alias for isSelected) */
  mantineInput?: CheckboxMantineInputProps
} & Omit<
  AriaCheckboxProps,
  'className' | 'style' | 'isSelected' | 'onChange' | 'children'
> &
  StyleProps &
  Pick<VariantProps<typeof checkboxStyles>, never>

export const UICheckbox = forwardRef<HTMLLabelElement, UICheckboxProps>(
  function UICheckbox(props, ref) {
    const {
      className,
      style,
      children,
      description,
      isSelected,
      onChange,
      mantineInput,
      ...rest
    } = props

    // Support both react-aria pattern (isSelected) and Mantine pattern (value)
    const isChecked = isSelected ?? mantineInput?.value ?? false

    // Handle onChange to support both patterns
    const handleChange = (selected: boolean) => {
      onChange?.(selected)
      mantineInput?.onChange?.(selected)
    }

    const styles = checkboxStyles({
      hasDescription: Boolean(description),
    })

    return (
      <AriaCheckbox
        ref={ref}
        className={styles.root({ className })}
        style={style}
        isSelected={isChecked}
        onChange={handleChange}
        {...rest}
      >
        {composeRenderProps(children, children => (
          <>
            <div className={styles.checkbox()}>
              <MaterialSymbolsCheck className={styles.checkIcon()} />
            </div>
            <div className={styles.content()}>
              {children && <div className={styles.label()}>{children}</div>}
              {description && (
                <div className={styles.description()}>{description}</div>
              )}
              {mantineInput?.error && (
                <div className={styles.error()}>{mantineInput.error}</div>
              )}
            </div>
          </>
        ))}
      </AriaCheckbox>
    )
  },
)
